/** Shadow mode (SPEC §34.1, phase P3). */
import { z } from 'zod';
import { BUILD_NUMBER_MAX, Platform, uuid } from './common.js';
import { envKey } from './env-vars.js';
import { CiContext, CiProvider, GitRef } from './git.js';

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/**
 * `POST /shadow-runs`: what a legacy pipeline step did, sent by `hangar shadow` right after its `eas build`. The API
 * computes Hangar's dry-run plan for the same app/profile/environment/inputs, compares, and stores the differences.
 * `observed.env` values are used in memory to compute HMACs and are never stored, logged or returned.
 */
export const ShadowRunCreateRequest = z.object({
  app: z.string().min(1).max(128),
  project: z.string().max(128).optional(),
  profile: z.string().min(1).max(64),
  environment: z.string().min(1).max(64),
  platform: Platform,
  git: GitRef,
  /** Launch-form inputs the Hangar build would receive (e.g. `track` for `release-android-to-track`). */
  inputs: z.record(z.string(), z.unknown()).default({}),
  /** provider + pipelineUuid + stepUuid (+ platform) is the run's idempotency key, so they are required here. */
  ciContext: CiContext.extend({
    provider: CiProvider,
    pipelineUuid: z.string().min(1).max(64),
    stepUuid: z.string().min(1).max(64),
  }),
  observed: z.object({
    providerBuildId: z.string().min(1).max(128).optional(),
    providerProfile: z.string().min(1).max(64),
    /** Store track the pipeline submitted to; null when it did not submit. */
    track: z.string().min(1).max(64).nullable().default(null),
    versionName: z.string().min(1).max(64).nullable().default(null),
    /** Can raise the app's build-number floor, which never goes down: at most `BUILD_NUMBER_MAX`. */
    buildNumber: z.number().int().min(0).max(BUILD_NUMBER_MAX).nullable().default(null),
    /** The env the provider build received (eas.json profile env over EAS environment variables). */
    env: z.record(envKey, z.string().max(65536)).refine((e) => Object.keys(e).length <= 500, 'at most 500 variables'),
    /** Provider variables the pipeline cannot read (EAS `secret` visibility). */
    unreadableKeys: z.array(envKey).max(500).default([]),
    /** Labels of the merged sources, e.g. `eas env:pull preview`, `apps/mobile/eas.json#build.testing.env`. */
    envSources: z.array(z.string().min(1).max(200)).max(20).default([]),
  }),
});
export type ShadowRunCreateRequest = z.infer<typeof ShadowRunCreateRequest>;

export const ShadowRunListQuery = z.object({
  app: z.string().max(128).optional(),
  project: z.string().max(128).optional(),
  projectId: uuid.optional(),
  status: z.enum(['match', 'differs']).optional(),
  source: z.enum(['pipeline', 'nightly']).optional(),
  day: day.optional(),
  since: day.optional(),
  cursor: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ShadowRunListQuery = z.infer<typeof ShadowRunListQuery>;

/** `PATCH /apps/{appId}/shadow` (merge patch, `If-Match` = the app's version). */
export const ShadowSettingsPatch = z.object({
  enabled: z.boolean().optional(),
  platforms: z.array(Platform).min(1).max(2).optional(),
  requiredDays: z.number().int().min(1).max(90).optional(),
  minRuns: z.number().int().min(0).max(10000).optional(),
  ignoreKeys: z.array(z.string().min(1).max(128)).max(100).optional(),
}).refine((p) => Object.keys(p).length > 0, 'at least one field');
export type ShadowSettingsPatch = z.infer<typeof ShadowSettingsPatch>;

/** Defaults when shadow mode is first enabled (SPEC §33 P3: 2 weeks with 0 differences). */
export const SHADOW_DEFAULTS = { requiredDays: 14, minRuns: 5, ignoreKeys: ['EAS_BUILD_*', 'EAS_PROJECT_*', 'BITBUCKET_*', 'CI', 'EXPO_TOKEN'] } as const;
